import { apiDownloadBlob } from './client.js';

/**
 * Tenant data export (ADR-0034, Retention Phase 2a).
 *
 * `POST /v1/export/me` — owner/admin, re-authenticated with the caller's own
 * password, returning the bundle as a JSON attachment. The bundle is generated
 * for this one request and stored nowhere, so a second copy means a second
 * request (and a second re-auth, and a second audit row).
 *
 * Three things about this call are deliberate:
 *
 *  - **POST, with a body.** The password cannot travel in a URL — it would land
 *    in browser history, proxy logs and the referrer. This is why
 *    `apiDownloadBlob` grew an options argument.
 *  - **`skipRefresh: true`.** A 401 from this route means *"that password is
 *    wrong"*, not *"your access token expired"*. Without this the client would
 *    re-send the wrong password and, if the refresh also failed, sign the user
 *    out for a typo. See `ApiDownloadOptions.skipRefresh`.
 *  - **Blob, not JSON.** The caller saves the file; nothing parses or keeps the
 *    contents in memory longer than the save takes. This is the single most
 *    sensitive payload the API emits.
 */
export function downloadTenantExport(
  currentPassword: string,
): Promise<{ blob: Blob; filename: string }> {
  return apiDownloadBlob('/v1/export/me', 'lumohr-export.json', {
    method: 'POST',
    body: { currentPassword },
    skipRefresh: true,
  });
}

/**
 * The server's own code for a rejected password (`routes/export.ts`). A 401
 * carrying it is a credential failure; any other 401 is a session failure, and
 * the two must not be reported with the same sentence — telling someone their
 * password is wrong when their session expired sends them to reset a password
 * that was never the problem.
 */
export const INVALID_CREDENTIALS = 'invalid_credentials';

export function isWrongPassword(body: unknown): boolean {
  return (
    typeof body === 'object' &&
    body !== null &&
    'error' in body &&
    (body as { error: unknown }).error === INVALID_CREDENTIALS
  );
}
